import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
import { formValueSelector } from 'redux-form'
// components
import { MultiValueLabel, ReduxEntitySelect } from 'components/FormElements'
import OpenRiskSingleValue from 'components/FormElements/components/OpenRiskLink/SingleValue'
// view components
import UserDetails from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/Lookup/components/UserDetails'
// types
import { Forms } from 'constants/forms'
import { GroupBase } from 'react-select'
import { ValidatorFn } from 'utils/validators'
import { FieldSource, IField } from 'types/graphql'
import { CustomSelectProps, IOption } from 'components/FormElements/Select'
// context
import { PublicContext } from 'containers/Risks/Risk/Edit/public'
import { ImportedRiskContext } from 'components/Modals/ImportedRiskModal'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// views
import { LookupWrapper } from './views'
// utils
import { getOptions } from './utils'
import { formatOptionLabel, getEntitySelectQuery, getEntitySelectVariables } from 'utils/fields'

interface Props extends CustomSelectProps {
  name: string
  field: IField
  form?: string
  label?: string
  disabled?: boolean
  validate?: ValidatorFn
  isTableField?: boolean
  prepareOptions?: (options: (IOption | GroupBase<IOption>)[]) => (IOption | GroupBase<IOption>)[]
  canSetPermissions?: boolean
}

const Lookup: React.FC<Props> = ({
  name,
  field,
  label,
  form = Forms.RISK_TEMPLATE_FORM,
  disabled = false,
  isTableField,
  prepareOptions,
  canSetPermissions = false,
  ...props
}) => {
  const router = useRouter()

  const isPublic = React.useContext(PublicContext)

  const { isImportedField } = React.useContext(ImportedRiskContext)
  const { riskTemplate, isRiskTemplate, isRisk } = React.useContext(RiskTemplateAPIContext)

  const fieldValue = useSelector((state) => formValueSelector(form)(state, field.id))

  const domain = router.query.domain as string
  const isFilter = !isImportedField && router?.pathname === '/forms'
  const limitResponse = field.limitResponse?.value
  const searchTemplateIds = field.searchTemplateIds ? [field.id] : null
  const sources = searchTemplateIds ? [...field.source, FieldSource.Risks] : field.source
  const query = getEntitySelectQuery(sources, isPublic)

  const variables = getEntitySelectVariables({
    domain,
    sources,
    limitResponse,
    searchTemplateIds
  })

  const handleSearch = ({ search, refetch }) => {
    refetch(
      getEntitySelectVariables({
        domain,
        search,
        sources,
        limitResponse,
        searchTemplateIds
      })
    )
  }

  const getOptionValue = (option: IOption) => {
    if (option.sourceFieldId) {
      return `${option.value}-${option.sourceFieldId}`
    }
    return option.relatedFieldId ? `${option.value}-${option.relatedFieldId}` : option.value
  }

  const showDetails = field.showUserDetails && isRisk && !isPublic && !isTableField

  return (
    <LookupWrapper data-testid='risk-field-value' data-entity-id={field.id}>
      <ReduxEntitySelect
        isGrouped
        name={name}
        query={query}
        field={field}
        label={label || field.label}
        source={sources}
        variables={variables}
        isDisabled={disabled}
        required={field.required}
        handleSearch={handleSearch}
        isMultiple={field.multiple}
        prepareOptions={prepareOptions}
        getOptionValue={getOptionValue}
        formatOptionLabel={formatOptionLabel}
        canSetPermissions={canSetPermissions}
        getOptions={getOptions({ field, isFilter, riskTemplate, isRiskTemplate })}
        isClearable={!field.multiple && !field.required}
        components={{
          MultiValueLabel,
          ...(searchTemplateIds && !field.multiple && !isPublic && { SingleValue: OpenRiskSingleValue })
        }}
        {...props}
      />
      {showDetails && <UserDetails fields={field.visibleUserDetailsFields} userId={fieldValue?.value?.value} />}
    </LookupWrapper>
  )
}

export default Lookup
